/**
 * ResultsDisplay Component
 * Displays the personal best time result with machine selection
 * Shows overall best by default with buttons to view each machine's PB
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

function ResultsDisplay({ results, rider, compactMode = false }) {
  // State to track which machine is selected (null = overall best)
  const [selectedMachine, setSelectedMachine] = useState(null);
  
  // Determine which result to display
  const displayResult = selectedMachine !== null 
    ? results.machines[selectedMachine]
    : results.overall_best;

  /**
   * Format finish time to display with appropriate precision
   */
  const formatTime = (time) => {
    if (!time) return 'N/A';
    return `${time.toFixed(2)}s`;
  };

  /**
   * Get active classes for the run
   * All bikes are in Open class, plus any additional special classes
   */
  const getActiveClasses = () => {
    const classes = ['Open']; // All bikes are in Open class
    if (displayResult.class_rl) classes.push('RL');
    if (displayResult.class_vintage) classes.push('Vintage');
    if (displayResult.class_classic) classes.push('Classic');
    if (displayResult.class_fe) classes.push('FE');
    return classes.join(', ');
  };

  /**
   * Format machine details
   */
  const getMachineDetails = () => {
    const parts = [];
    if (displayResult.manufacturer) parts.push(displayResult.manufacturer);
    if (displayResult.model) parts.push(displayResult.model);
    if (displayResult.capacity) parts.push(`${displayResult.capacity}cc`);
    return parts.length > 0 ? parts.join(' ') : 'N/A';
  };

  /**
   * Generate event results URL
   */
  const getEventResultsUrl = () => {
    if (!displayResult.hill || !displayResult.date) return null;
    return `/results?hill=${encodeURIComponent(displayResult.hill)}&date=${displayResult.date}`;
  };

  return (
    <div className="results-container">
      {/* Header */}
      <div className="result-header">
        <span className="result-icon">🏆</span>
        <h3>Personal Best Time</h3>
      </div>

      {/* Rider and Hill Info */}
      <div className="result-info">
        <h2 className="rider-name">{rider.fullname}</h2>
        <p className="hill-name">{displayResult.hill}</p>
      </div>

      {/* Machine Selection Buttons */}
      {!compactMode && results.machines && results.machines.length > 1 && (
        <div className="machine-selector">
          <h4>Select Machine:</h4>
          <div className="machine-buttons">
            <button
              className={selectedMachine === null ? 'active' : ''}
              onClick={() => setSelectedMachine(null)}
            >
              Overall Best
            </button>
            {results.machines.map((machine, index) => (
              <button
                key={index}
                className={selectedMachine === index ? 'active' : ''}
                onClick={() => setSelectedMachine(index)}
              >
                #{machine.race_number} - {machine.manufacturer} {machine.capacity}cc
                <br />
                <span className="machine-time">{formatTime(machine.finish_time)}</span>
                {machine.run_type && <span className="machine-run-type"> ({machine.run_type})</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Time Display */}
      <div className="time-display">
        <div className="time-label">PERSONAL BEST TIME</div>
        <div className="time-value">{formatTime(displayResult.finish_time)}</div>
      </div>

      {/* Event Details */}
      <div className="result-details">
        <div className="detail-section">
          <h4>Event Details</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Event:</span>
              {getEventResultsUrl() ? (
                <Link to={getEventResultsUrl()} className="event-link">
                  {displayResult.event_name || 'N/A'}
                </Link>
              ) : (
                <span className="detail-value">{displayResult.event_name || 'N/A'}</span>
              )}
            </div>
            <div className="detail-item">
              <span className="detail-label">Run Type:</span>
              <span className="detail-value">{displayResult.run_type || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Weather:</span>
              <span className="detail-value">{displayResult.weather || ''}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Track Condition:</span>
              <span className="detail-value">{displayResult.track_condition || ''}</span>
            </div>
          </div>
        </div>

        {/* Machine Details */}
        <div className="detail-section">
          <h4>Machine Details</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Machine:</span>
              <span className="detail-value">{getMachineDetails()}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Type:</span>
              <span className="detail-value">{displayResult.machine_type || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Race Number:</span>
              <span className="detail-value">{displayResult.race_number || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Classes:</span>
              <span className="detail-value">{getActiveClasses()}</span>
            </div>
          </div>
        </div>

        {/* Passenger Info (if applicable) */}
        {(displayResult.passenger_firstname || displayResult.passenger_lastname) && (
          <div className="detail-section">
            <h4>Passenger</h4>
            <div className="detail-item">
              <span className="detail-value">
                {displayResult.passenger_firstname} {displayResult.passenger_lastname}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultsDisplay;
